import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';

import { dataLayerPush } from '@r1-frontend/analytics/dataLayerPush';

import { BaseButton, btnTypes } from '@r1-frontend/ui-react/components/buttons/baseButton';
import { CATEGORIES } from '@r1-frontend/shared/constants/analytics';

import { TariffCardPlug } from '~/src/components/TariffCardPlug';
import TariffLine, { ESortingKeys } from '~/src/components/TariffLine';
import { TariffsFilters } from '~/src/components/TariffsFilters';
import { ETariffTypes } from '~/src/components/TariffsFilters/filterConstants';
import { ITariff } from '~/src/entities/formattedTariff';
import { TAB_FLAT, TAB_VILLAGE } from '~/src/features/index/Tariffs/constants';
import { EPrivateHouse } from '~/src/features/internet/internet-represent';
import { useOptimizationToponymForInternetPage } from '~/src/helpers/city/useOptimizationToponym';
import { selectAvailableOnInternetPageTariffs } from '~/src/store/selectors/filteredBundles';

import * as ST from './styled';

const sendGTMevent = (actionText: string, label?: string) => {
    dataLayerPush({
        event: 'UAevent',
        category: CATEGORIES.tariffCards,
        action: actionText,
        label: label || '',
    });
};

const PATH_LINK_CITY = '/internet';
const PATH_LINK_VILLAGE = '/internet/mono-village';

export interface TariffsProps {
    privateHouse?: EPrivateHouse,
    openChannelsPopup: (tariff: ITariff) => void,
    isAuth?: boolean,
}

export const Tariffs = ({ privateHouse, openChannelsPopup, isAuth }: TariffsProps): JSX.Element => {
    const router = useRouter();
    const initTariffs = useSelector(selectAvailableOnInternetPageTariffs({ isCottage: !!privateHouse }));
    const toponymOptimization  = useOptimizationToponymForInternetPage();

    const menuItems = useMemo(() => ([
        {
            text: TAB_FLAT,
            action: () => {
                router.push(PATH_LINK_CITY);
                sendGTMevent('click apartment');
            },
        },
        {
            text: TAB_VILLAGE,
            action: () => {
                router.push(PATH_LINK_VILLAGE);
                sendGTMevent('click village');
            },
        },
    ]), [router]);

    return (
        <ST.Tariffs>
            <ST.StyledGridContainer>
                <ST.Title>
                    Тарифы интернета{toponymOptimization}
                </ST.Title>
            </ST.StyledGridContainer>
            <ST.StyledGridContainer>
                <TariffsFilters
                    isVillage={!!privateHouse}
                    initTariffs={initTariffs}
                    defaultValue={ETariffTypes.FILTER_BUNDLE}
                    menuItems={menuItems}
                    hasCityTariffs={true}
                    hasVillageTariffs={true}
                >
                    {filteredTariffs => {
                        return filteredTariffs.length > 0
                            ? <TariffLine
                                sortField={ESortingKeys.INTERNET}
                                baseTariffs={filteredTariffs}
                                openPopup={openChannelsPopup}
                                isAuth={isAuth}
                            />
                            : <TariffCardPlug/>;
                    }}
                </TariffsFilters>
            </ST.StyledGridContainer>
            <ST.StyledGridContainer>
                <ST.BtnWrapper>
                    <BaseButton
                        href='/bundles'
                        onClick={() => sendGTMevent('click view all')}
                        type={btnTypes.PRIMARY}
                    >
                        Больше тарифов
                    </BaseButton>
                </ST.BtnWrapper>
            </ST.StyledGridContainer>
        </ST.Tariffs>
    );
};
